import { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../config/api';
import { getLocationBasedLanguage } from '../utils/language';
import { trackEvent } from '../utils/analytics';
import { apiFetch, buildApiUrl, buildResponseError, parseResponseBody } from '../utils/http';

const LANGUAGE_STORAGE_KEY = 'loopino.language';

const resolveAuthErrorType = (error) => {
  const message = (error?.message || '').toLowerCase();
  if (!message) return 'unknown';
  if (message.includes('invalid') || message.includes('credentials')) return 'invalid_credentials';
  if (message.includes('timeout')) return 'timeout';
  if (message.includes('network')) return 'network';
  if (message.includes('rate')) return 'rate_limited';
  return 'unknown';
};

const extractUserFromResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  if (response.user && typeof response.user === 'object') {
    return response.user;
  }

  if (response.data?.user && typeof response.data.user === 'object') {
    return response.data.user;
  }

  if (response.data && typeof response.data === 'object' && (response.data.id || response.data.email)) {
    return response.data;
  }

  if (response.id || response.email) {
    return response;
  }

  return null;
};

const createApiCall = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    data = null,
    headers = {},
    isFormData = false
  } = options;

  const url = buildApiUrl(endpoint);

  console.log('[API] Creating API call', {
    timestamp: new Date().toISOString(),
    endpoint,
    fullUrl: url,
    method,
    hasData: !!data
  });

  const response = await apiFetch(endpoint, {
    method,
    headers,
    data,
    isFormData
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401 && endpoint.includes('/auth/login')) {
      throw new Error(responseBody?.message || 'Invalid credentials');
    }

    throw buildResponseError(response, responseBody);
  }

  const normalizedBody =
    responseBody && typeof responseBody === 'object'
      ? responseBody
      : (responseBody === null ? {} : { data: responseBody });

  return {
    ...normalizedBody,
    _status: response.status,
    _statusText: response.statusText
  };
};

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyUserSession = useCallback((nextUser) => {
    if (nextUser) {
      setUser(nextUser);
      setIsAuthenticated(true);
      setError(null);
      if (nextUser.language) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, nextUser.language);
      }
      return nextUser;
    }

    setUser(null);
    setIsAuthenticated(false);
    return null;
  }, []);

  const clearAuthState = useCallback(() => {
    console.log('[AUTH] Clearing authentication state', {
      timestamp: new Date().toISOString(),
      currentUser: user?.id,
      wasAuthenticated: isAuthenticated
    });

    setUser(null);
    setIsAuthenticated(false);
    setError(null);

    localStorage.removeItem('companyLogoPreview');
    localStorage.removeItem('companyLogoName');
    localStorage.removeItem('companyLogoObjectFit');
    localStorage.removeItem('companyLogoCompanyId');
    localStorage.removeItem('companyLogoRemoteUrl');
    localStorage.removeItem('companyLogoPreviewUpdatedAt');
  }, [isAuthenticated, user?.id]);

  const refreshSession = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.ME, {
        method: 'GET'
      });

      const sessionUser = extractUserFromResponse(response);
      applyUserSession(sessionUser);
      return sessionUser;
    } catch (sessionError) {
      if (sessionError.status === 401) {
        applyUserSession(null);
        if (!silent) {
          setError(null);
        }
        return null;
      }

      throw sessionError;
    }
  }, [applyUserSession]);

  const setLocalUserLanguage = useCallback((language) => {
    if (!language) {
      return null;
    }

    setUser((prevUser) => (prevUser ? { ...prevUser, language } : prevUser));
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    return language;
  }, []);

  const updateUserLanguage = useCallback(async (language) => {
    if (!language) {
      return null;
    }

    if (!isAuthenticated) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      return language;
    }

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.USER.LANGUAGE_SET, {
        method: 'PUT',
        data: { language }
      });

      const resolvedLanguage = response?.language || response?.data?.language || language;
      setLocalUserLanguage(resolvedLanguage);
      return resolvedLanguage;
    } catch (languageError) {
      console.error('[AUTH] Failed to update user language', languageError);
      throw languageError;
    }
  }, [isAuthenticated, setLocalUserLanguage]);

  const refreshUserLanguage = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.USER.LANGUAGE_GET, {
        method: 'GET'
      });

      const resolvedLanguage = response?.language || response?.data?.language;
      if (resolvedLanguage) {
        setLocalUserLanguage(resolvedLanguage);
      }
      return resolvedLanguage;
    } catch (languageError) {
      console.error('[AUTH] Failed to fetch user language', languageError);
      throw languageError;
    }
  }, [isAuthenticated, setLocalUserLanguage]);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      setIsLoading(true);

      try {
        await refreshSession({ silent: true });
      } catch (bootstrapError) {
        console.error('[AUTH] Failed to bootstrap session', bootstrapError);
        if (isMounted) {
          setError(bootstrapError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, [refreshSession]);

  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[AUTH] Auth expiration event received, performing full logout');
      clearAuthState();
      setError('Session expired. Please login again.');
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, [clearAuthState]);

  useEffect(() => {
    if (!isAuthenticated || user?.language) {
      return;
    }

    refreshUserLanguage().catch((languageError) => {
      console.warn('[AUTH] Unable to fetch user language on init', languageError);
    });
  }, [isAuthenticated, refreshUserLanguage, user?.language]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const syncSessionAfterAuth = useCallback(async (fallbackResponse = null) => {
    const sessionUser = await refreshSession();
    const fallbackUser = extractUserFromResponse(fallbackResponse);
    const effectiveUser = sessionUser || fallbackUser;

    if (effectiveUser) {
      applyUserSession(effectiveUser);
    }

    return {
      ...(fallbackResponse && typeof fallbackResponse === 'object' ? fallbackResponse : {}),
      ...(effectiveUser ? { user: effectiveUser } : {})
    };
  }, [applyUserSession, refreshSession]);

  const login = useCallback(async (credentials) => {
    console.log('[AUTH] Login attempt started', {
      timestamp: new Date().toISOString(),
      endpoint: API_CONFIG.ENDPOINTS.AUTH.LOGIN,
      hasPassword: !!credentials.password,
      loginField: credentials.login || credentials.email || credentials.username,
      credentialKeys: Object.keys(credentials),
      isDevMode: process.env.REACT_APP_DEV_MODE === 'true'
    });

    if (process.env.REACT_APP_DEV_MODE === 'true' && credentials.isDev) {
      const mockUser = {
        id: 'dev-user-123',
        email: credentials.login || 'dev@loopino.com',
        username: 'dev_user',
        first_name: 'Dev',
        last_name: 'User',
        created_at: new Date().toISOString(),
        is_verified: true,
        language: localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'it'
      };

      applyUserSession(mockUser);
      trackEvent('login', { method: 'dev' });
      return { user: mockUser };
    }

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        data: credentials
      });

      const resolvedResponse = await syncSessionAfterAuth(response);
      const preferredLanguage = getLocationBasedLanguage(LANGUAGE_STORAGE_KEY);
      const currentLanguage = resolvedResponse?.user?.language;

      if (preferredLanguage && preferredLanguage !== currentLanguage) {
        try {
          const updatedLanguage = await updateUserLanguage(preferredLanguage);
          if (updatedLanguage && resolvedResponse.user) {
            resolvedResponse.user = {
              ...resolvedResponse.user,
              language: updatedLanguage
            };
          }
        } catch (languageError) {
          console.warn('[AUTH] Unable to sync language preference after login', languageError);
        }
      }

      trackEvent('login', { method: 'password' });
      return resolvedResponse;
    } catch (loginError) {
      console.error('[AUTH] Login failed', loginError);
      trackEvent('login_error', { error_type: resolveAuthErrorType(loginError) });
      setError(loginError.message);
      throw loginError;
    }
  }, [applyUserSession, syncSessionAfterAuth, updateUserLanguage]);

  const loginWithGoogle = useCallback(async (idToken) => {
    if (!idToken) {
      const missingTokenError = new Error('Missing Google ID token');
      setError(missingTokenError.message);
      throw missingTokenError;
    }

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.GOOGLE, {
        method: 'POST',
        data: { id_token: idToken }
      });

      const resolvedResponse = await syncSessionAfterAuth(response);
      const preferredLanguage = getLocationBasedLanguage(LANGUAGE_STORAGE_KEY);
      const currentLanguage = resolvedResponse?.user?.language;

      if (preferredLanguage && preferredLanguage !== currentLanguage) {
        try {
          const updatedLanguage = await updateUserLanguage(preferredLanguage);
          if (updatedLanguage && resolvedResponse.user) {
            resolvedResponse.user = {
              ...resolvedResponse.user,
              language: updatedLanguage
            };
          }
        } catch (languageError) {
          console.warn('[AUTH] Unable to sync language preference after Google login', languageError);
        }
      }

      trackEvent('login', { method: 'google' });
      return resolvedResponse;
    } catch (googleError) {
      console.error('[AUTH] Google login failed', googleError);
      trackEvent('login_error', { error_type: resolveAuthErrorType(googleError) });
      setError(googleError.message);
      throw googleError;
    }
  }, [syncSessionAfterAuth, updateUserLanguage]);

  const register = useCallback(async (userData) => {
    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.REGISTER, {
        method: 'POST',
        data: userData
      });

      try {
        const resolvedResponse = await syncSessionAfterAuth(response);
        trackEvent('sign_up', { method: 'email' });
        return resolvedResponse;
      } catch (sessionError) {
        if (sessionError.status === 401) {
          trackEvent('sign_up', { method: 'email' });
          return response;
        }
        throw sessionError;
      }
    } catch (registerError) {
      console.error('[AUTH] Registration failed', registerError);
      trackEvent('sign_up_error', { error_type: resolveAuthErrorType(registerError) });
      throw registerError;
    }
  }, [syncSessionAfterAuth]);

  const logout = useCallback(async () => {
    trackEvent('logout');

    try {
      await createApiCall(API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {
        method: 'POST'
      });
    } catch (logoutError) {
      console.error('[AUTH] Logout API call failed (will clear local state anyway)', logoutError);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const refreshToken = useCallback(async () => {
    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.REFRESH, {
        method: 'POST'
      });

      await refreshSession();

      try {
        await refreshUserLanguage();
      } catch (languageError) {
        console.warn('[AUTH] Unable to refresh language after token refresh', languageError);
      }

      return response;
    } catch (refreshError) {
      console.error('[AUTH] Token refresh failed', refreshError);
      clearAuthState();
      throw refreshError;
    }
  }, [clearAuthState, refreshSession, refreshUserLanguage]);

  const changePassword = useCallback(async (passwordData) => (
    createApiCall(API_CONFIG.ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      method: 'POST',
      data: passwordData
    })
  ), []);

  const forgotPassword = useCallback(async (email) => (
    createApiCall(API_CONFIG.ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      data: { email }
    })
  ), []);

  const resetPassword = useCallback(async (resetData) => (
    createApiCall(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      data: resetData
    })
  ), []);

  const verifyEmail = useCallback(async (token) => {
    const response = await createApiCall(API_CONFIG.ENDPOINTS.AUTH.VERIFY_EMAIL, {
      method: 'POST',
      data: { token }
    });
    trackEvent('verify_email');
    return response;
  }, []);

  const updateUser = useCallback(async (userData) => {
    const response = await createApiCall('/api/v1/auth/profile', {
      method: 'PUT',
      data: userData
    });

    const nextUser = extractUserFromResponse(response);
    if (nextUser) {
      applyUserSession(nextUser);
    }

    return response;
  }, [applyUserSession]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshToken,
    refreshSession,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    updateUser,
    updateUserLanguage,
    refreshUserLanguage,
    setLocalUserLanguage,
    clearAuth: clearAuthState,
    clearError
  };
};

export default useAuth;
